import * as React from "react";
import { useState } from "react";
import { Check, ShoppingCart, Minus, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import CheckoutButton from './CheckoutButton';
import { ProductProps, ProductSize, Variant } from '../data/products';

interface ProductShowcaseProps {
  product: ProductProps;
  className?: string;
}

export function ProductShowcase({ product, className }: ProductShowcaseProps) {
  const [selectedSizeId, setSelectedSizeId] = useState<string>(product.sizes[0]?.id || '');
  
  // Initialize selectedVariantId based on the first variant of the first size, if available
  const initialSize = product.sizes.find(s => s.id === selectedSizeId);
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(
    initialSize?.variants?.[0]?.id || null
  );
  const [quantity, setQuantity] = useState(1);

  const selectedSize = product.sizes.find(size => size.id === selectedSizeId);
  const selectedVariant = selectedSize?.variants?.find((variant: Variant) => variant.id === selectedVariantId);

  const handleSizeChange = (sizeId: string) => {
    setSelectedSizeId(sizeId);
    // Reset variant selection when size changes, default to first variant if available
    const newSize = product.sizes.find(s => s.id === sizeId);
    setSelectedVariantId(newSize?.variants?.[0]?.id || null);
    setQuantity(1);
  };

  const handleVariantChange = (variantId: string) => {
    setSelectedVariantId(variantId);
    setQuantity(1);
  }; 
 
  const incrementQuantity = () => setQuantity(prev => prev + 1);
  const decrementQuantity = () => setQuantity(prev => Math.max(1, prev - 1));

  // Determine the final selection details (variant takes precedence)
  const finalSelection = selectedVariant || selectedSize;

  // Determine which image to display prominently
  const displayImageUrl = selectedVariant?.image || selectedSize?.image || product.images[0]?.url || '/placeholder.svg';
  const displayImageAlt = selectedVariant?.name || selectedSize?.name || product.name;

  return (
    <section className={cn("container py-12 mx-auto", className)}>
      <div className="flex flex-col lg:flex-row gap-12 items-start">
        {/* Left Column: Product Image */}
        <div className="w-full lg:w-1/2">
          <Card className="overflow-hidden">
            <CardContent className="p-0 aspect-square flex items-center justify-center">
              <img
                src={displayImageUrl}
                alt={displayImageAlt}
                className="object-contain w-full h-full max-h-[500px]"
              />
            </CardContent>
          </Card>
        </div>
        <div className="w-full lg:w-1/2 lg:pl-10">
          <h1 className="mb-2 text-3xl font-bold tracking-tight sm:text-4xl">
            {product.name}
          </h1>
          {/* Add treatment capacity subtitle */}
          {(selectedVariant?.treatmentCapacity || selectedSize?.treatmentCapacity) && (
            <h2 className="mb-4 text-xl text-marine font-semibold">
              {selectedVariant?.treatmentCapacity || selectedSize?.treatmentCapacity}
            </h2>
          )}
          <div className="mb-6 prose text-muted-foreground">
            <p>{product.description}</p>
          </div>
          {/* Size Selection */}
          <div className="flex items-center gap-4 mb-6">
            <h3 className="text-lg font-semibold">Select Size:</h3>
            <div className="flex flex-wrap gap-2">
              {product.sizes.map((size: ProductSize) => (
                <Button
                  key={size.id}
                  variant={selectedSizeId === size.id ? "default" : "outline"}
                  onClick={() => handleSizeChange(size.id)}
                  disabled={!size.inStock && (!size.variants || size.variants.every(v => !v.inStock))}
                >
                  {size.name}
                  {!size.inStock && (!size.variants || size.variants.every(v => !v.inStock)) && <Badge variant="destructive" className="ml-2">Sold Out</Badge>}
                </Button>
              ))}
            </div>
          </div>
          {/* Variant Selection - Only show if selected size has variants */}
          {selectedSize && selectedSize.variants && selectedSize.variants.length > 0 && (
            <div className="flex items-center gap-4 mb-6">
              <h3 className="text-lg font-semibold">Select Option:</h3>
              <div className="flex flex-wrap gap-2">
                {selectedSize.variants.map((variant: Variant) => (
                  <Button
                    key={variant.id}
                    variant={selectedVariantId === variant.id ? "default" : "outline"}
                    onClick={() => handleVariantChange(variant.id)}
                    disabled={!variant.inStock}
                  >
                    {variant.name}
                    {!variant.inStock && <Badge variant="destructive" className="ml-2">Sold Out</Badge>}
                  </Button>
                ))}
              </div>
            </div>
          )}
          {/* Dynamic Price Display */}
          <div className="mb-6 text-3xl font-bold">
            {
              // Display variant price if selected, otherwise size price
              selectedVariant ? (
                `${product.currency || '$'}${selectedVariant.price.toFixed(2)}`
              ) : selectedSize ? (
                `${product.currency || '$'}${selectedSize.price.toFixed(2)}`
              ) : (
                <span className="text-gray-500">Select size</span> // Placeholder if nothing selected
              )
            }
          </div>
          {/* Quantity Selector */}
          <div className="flex items-center gap-4 mb-6">
            <h3 className="text-lg font-semibold">Quantity:</h3>
            <div className="flex items-center p-1 border rounded-md">
              <Button variant="ghost" size="icon" onClick={decrementQuantity} disabled={quantity <= 1}>
                <Minus className="w-4 h-4" />
              </Button>
              <span className="w-10 text-center">{quantity}</span>
              <Button variant="ghost" size="icon" onClick={incrementQuantity}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>
          {/* Add to Cart Button & Stock Status */}
          <div className="mt-8">
            <CheckoutButton 
              priceId={selectedVariant ? selectedVariant.priceId : selectedSize?.priceId || ''}
              quantity={quantity}
              disabled={!finalSelection || !finalSelection.inStock}
            />
            <div className="mt-4 text-sm text-gray-600">
              {finalSelection ? (
                finalSelection.inStock ? (
                  <span className="inline-flex items-center text-green-600">
                    <Check className="w-4 h-4 mr-1" /> In Stock
                  </span>
                ) : (
                  <span className="inline-flex items-center text-red-600">
                    <Badge variant="destructive">Sold Out</Badge>
                  </span>
                )
              ) : (
                <span className="text-gray-500">Select size/option</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default ProductShowcase;
